import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ShieldCheck, ArrowRight, Plus, X } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

const COMMON_SYMPTOMS = [
  "Fever", "Cough", "Headache", "Chest pain", "Shortness of breath",
  "Abdominal pain", "Back pain", "Fatigue", "Nausea", "Rash",
  "Dizziness", "Joint pain", "Sore throat", "Vomiting",
];

const RISK_STYLES = {
  low: "border-secondary text-secondary bg-secondary/8",
  moderate: "border-[#EAB308] text-[#854D0E] bg-[#EAB308]/10",
  critical: "border-destructive text-destructive bg-destructive/8",
};

export default function PatientTriage() {
  const [symptoms, setSymptoms] = useState([]);
  const [input, setInput] = useState("");
  const [duration, setDuration] = useState(1);
  const [severity, setSeverity] = useState("mild");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const addSymptom = (s) => {
    const val = (s || input).trim();
    if (!val) return;
    if (symptoms.includes(val)) return;
    setSymptoms((prev) => [...prev, val]);
    setInput("");
  };

  const remove = (s) => setSymptoms((prev) => prev.filter((x) => x !== s));

  const submit = async () => {
    if (symptoms.length === 0) {
      toast.error("Add at least one symptom");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/patient/triage", { symptoms, duration_days: duration, severity });
      setResult(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell
      pageTitle="Symptom triage"
      pageDescription="A rule-based check that helps you decide the right next step. Not a replacement for medical advice."
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-lg border border-border bg-white p-6">
            <div className="overline mb-3">Symptoms</div>
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a symptom and press Add"
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSymptom())}
                data-testid="triage-symptom-input"
              />
              <Button onClick={() => addSymptom()} data-testid="triage-add-symptom"><Plus size={14} className="mr-1" /> Add</Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="triage-symptom-chips">
              {symptoms.map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/5 text-primary px-2.5 py-1 text-xs">
                  {s}
                  <button onClick={() => remove(s)} className="opacity-70 hover:opacity-100" data-testid={`triage-chip-remove-${s}`}>
                    <X size={12} />
                  </button>
                </span>
              ))}
              {symptoms.length === 0 && <span className="text-xs text-muted-foreground">No symptoms yet.</span>}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duration">Duration (days)</Label>
                <Input id="duration" type="number" min={1} value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="triage-duration" />
              </div>
              <div>
                <Label htmlFor="severity">Severity</Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger id="severity" data-testid="triage-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mild">Mild</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="severe">Severe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={submit} className="mt-6 w-full h-11" disabled={loading} data-testid="triage-submit">
              {loading ? "Analyzing…" : "Get guidance"}
            </Button>
          </div>

          <div className="rounded-lg border border-border bg-muted/50 p-6">
            <div className="overline mb-3">Common symptoms</div>
            <div className="flex flex-wrap gap-2">
              {COMMON_SYMPTOMS.map((s) => (
                <button
                  key={s}
                  onClick={() => addSymptom(s)}
                  className="text-xs rounded-full border border-border bg-white px-3 py-1 hover:border-primary hover:text-primary transition-colors"
                  data-testid={`triage-suggested-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {result ? (
            <>
              <div className={`rounded-lg border p-6 ${RISK_STYLES[result.risk_level]}`} data-testid="triage-result">
                <div className="flex items-center gap-2">
                  {result.risk_level === "critical" ? <AlertTriangle size={18} /> : <ShieldCheck size={18} />}
                  <span className="overline">Risk · {result.risk_level}</span>
                </div>
                <div className="mt-3 font-serif text-2xl text-primary">See a {result.recommended_specialty}</div>
                <p className="mt-2 text-sm">{result.recommendation}</p>
                <div className="mt-4">
                  <Link to={`/directory`} className="inline-flex items-center gap-1 text-primary font-medium hover:underline" data-testid="triage-book-cta">
                    Book with a {result.recommended_specialty} <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
              <div className="rounded-lg border border-border bg-white p-6">
                <div className="overline mb-3">Self-care tips</div>
                <ul className="space-y-2 text-sm">
                  {result.self_care_tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-[11px] text-muted-foreground italic">{result.disclaimer}</p>
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
              Add symptoms and click <span className="font-medium text-primary">Get guidance</span> to see your triage result here.
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
